import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { CornerSpark, PearlStud, Sparkle } from './dialog/DialogOrnaments';
import { funnelMilestone } from '../api/funnel';

/**
 * Invite-friends dialogue — the game-level referral surface opened from the
 * Friends modal's "Invite a friend" CTA. Unlike the room-scoped
 * {@link ./wheel/InviteFriendSheet} (which drops a friend into one wheel
 * table), this shares a personal link to the GAME carrying the player's
 * referral code. The friend gets coins when they join; the player gets coins
 * and a few gems once the friend has played on two different days (producer
 * decision, 12 October 2026: paying for friends who really play stops
 * throwaway accounts from minting rewards).
 *
 * <p>Channels (producer pick): the system share sheet — one tap routes to
 * SMS / WhatsApp / Messenger / Facebook / email — plus copy-to-clipboard.
 * No dedicated Facebook button: Meta deprecated friend-invite APIs, so
 * "Share to Facebook" lives inside the OS sheet rather than a branded CTA.
 *
 * <p>The server grants both rewards ({@code InviteRewardService}): the
 * friend's when they redeem the code on first launch, the player's when a
 * background check sees the friend has played enough. The amounts here
 * mirror its {@code app.viral-invite.*} config.
 *
 * <p>Portals to {@code document.body} so it floats above the canvas + HUD.
 */
export interface InviteFriendsDialogProps {
  /** The player's stable id; the referral code is derived from it. Null in a
   *  cold/preview state still explains the offer but disables sharing. */
  playerId?: string | null;
  /** Coins the friend gets on joining. Mirrors the server's
   *  {@code viral-invite.coins-joiner}. */
  friendCoins?: number;
  /** Coins and gems the player gets once the friend has played. Mirror
   *  {@code viral-invite.coins-inviter} and {@code gems-inviter}. */
  yourCoins?: number;
  yourGems?: number;
  /** §6.E — open the "Schedule a game with friends" flow. When provided a
   *  secondary CTA surfaces it here (the natural "play with friends" home).
   *  Closes this dialogue first. Hidden when omitted. */
  onScheduleGame?: () => void;
  onClose: () => void;
}

/** Stable, shareable referral code derived from the player id (FNV-1a ->
 *  base36, "GT" + 6 chars). Deterministic, so a player's code never
 *  changes; the redemption backend maps it back to the inviter. */
export function referralCodeFor(playerId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < playerId.length; i++) {
    h ^= playerId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `GT${(h >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(-6)}`;
}

export function InviteFriendsDialog({
  playerId,
  friendCoins = 500,
  yourCoins = 500,
  yourGems = 10,
  onScheduleGame,
  onClose,
}: InviteFriendsDialogProps) {
  const [flash, setFlash] = useState<string | null>(null);
  const code = playerId ? referralCodeFor(playerId) : null;
  const url = code ? `https://goodtime.casino/i/${code}` : null;
  const theirs = `${friendCoins.toLocaleString()} coins`;
  const yours = `${yourCoins.toLocaleString()} coins and ${yourGems} gems`;

  const showFlash = (msg: string): void => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 1600);
  };

  const copy = (text: string, label: string): void => {
    // Playtest funnel — every copy in this dialog is an invite (referral code
    // or link), the desktop path where there's no native share. Once-per-device.
    funnelMilestone('invite_sent', playerId);
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).then(
        () => showFlash(label),
        () => showFlash(label),
      );
    } else {
      showFlash(label);
    }
  };

  const shareLink = (): void => {
    if (!url) return;
    // Playtest funnel — the player actually shared a referral invite.
    // Once-per-device, fire-and-forget; never blocks the share.
    funnelMilestone('invite_sent', playerId);
    const text = `Play Goodtime with me. You start with ${theirs} on me: ${url}`;
    if (navigator.share) {
      void navigator
        .share({ title: 'Goodtime', text, url })
        .then(() => showFlash('Shared'))
        .catch(() => {
          /* user dismissed the OS share sheet — no flash */
        });
    } else {
      copy(url, 'Link copied');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/75 backdrop-blur-sm"
      style={{ fontFamily: '"Fredoka", system-ui' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative z-10 mx-4 w-full max-w-sm">
        <div className="absolute inset-x-0 top-0 z-20 flex -translate-y-1/2 items-center justify-center">
          <div className="relative rounded-full border-[3px] border-amber-200/95 bg-gradient-to-b from-violet-400 to-violet-600 px-8 py-2 shadow-[0_4px_10px_rgba(0,0,0,0.5),inset_0_-2px_3px_rgba(40,12,80,0.55),inset_0_2px_3px_rgba(255,255,255,0.4)]">
            <PearlStud side="left" />
            <PearlStud side="right" />
            <h2 className="whitespace-nowrap text-base font-black tracking-wide text-white drop-shadow-[0_2px_0_rgba(60,20,90,0.75)]">
              INVITE FRIENDS
            </h2>
          </div>
        </div>

        <div
          className="relative overflow-hidden rounded-3xl border-[3px] border-amber-300/85 px-6 pb-6 pt-12 shadow-[0_20px_40px_rgba(0,0,0,0.6),inset_0_0_0_2px_rgba(255,210,90,0.15)]"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, #2A1352 0%, #1a0830 65%, #100520 100%)',
          }}
        >
          <CornerSpark position="top-left" />
          <CornerSpark position="top-right" />
          <CornerSpark position="bottom-left" />
          <CornerSpark position="bottom-right" />
          <Sparkle top="22%" left="10%" size={6} rotation={15} />
          <Sparkle top="58%" left="90%" size={7} rotation={-20} />

          {/* Reward hero */}
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="flex items-center gap-2 rounded-full bg-amber-300/10 px-4 py-1.5">
              <span className="text-2xl font-black text-amber-200">+{yourCoins.toLocaleString()}</span>
              <Icon name="coin" size={22} alt="coins" />
              <span className="text-2xl font-black text-amber-200">+{yourGems}</span>
              <Icon name="gem" size={22} alt="gems" />
            </div>
            <p className="mt-3 text-sm leading-snug text-white/85">
              Your friend gets <b className="text-amber-200">{theirs}</b> when
              they join with your link. You get{' '}
              <b className="text-amber-200">{yours}</b> once they've played on
              two different days.
            </p>
          </div>

          {/* Referral code (tap to copy) */}
          {code ? (
            <button
              type="button"
              onClick={() => copy(code, 'Code copied')}
              className="relative z-10 mt-5 flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3"
            >
              <span className="text-[11px] tracking-[0.4px] text-white/50">
                YOUR CODE
              </span>
              <span
                className="text-lg font-black tracking-[2px] text-amber-200"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {code}
              </span>
            </button>
          ) : (
            <p className="relative z-10 mt-5 text-center text-xs italic text-white/45">
              Your invite link is still loading.
            </p>
          )}

          {/* Channels: system share sheet + copy link */}
          <div className="relative z-10 mt-5 flex flex-col gap-3">
            <button
              type="button"
              onClick={shareLink}
              disabled={!url}
              className="flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-amber-200/95 bg-gradient-to-b from-amber-300 to-amber-500 py-2.5 text-base font-black tracking-wide text-[#3a1d00] shadow-[0_4px_10px_rgba(0,0,0,0.5),inset_0_-2px_3px_rgba(150,90,0,0.45),inset_0_2px_3px_rgba(255,255,255,0.55)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon name="share" size={16} alt="" />
              Share link
            </button>
            <button
              type="button"
              onClick={() => url && copy(url, 'Link copied')}
              disabled={!url}
              className="w-full rounded-full border-[3px] border-white/15 bg-white/[0.06] py-2.5 text-sm font-bold tracking-wide text-white/85 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Copy link
            </button>
          </div>

          {/* §6.E — schedule a game with friends. A quieter secondary path
              under the referral: pick a time, invite friends, drop
              into a shared wheel together. */}
          {onScheduleGame ? (
            <div className="relative z-10 mt-4 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={onScheduleGame}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-violet-300/40 bg-violet-400/10 py-2.5 text-sm font-bold tracking-wide text-violet-100"
              >
                <Icon name="clock" size={15} alt="" />
                Schedule a game with friends
              </button>
            </div>
          ) : null}

          <div className="relative z-10 mt-2 h-4 text-center">
            {flash ? (
              <span className="text-xs font-bold tracking-wide text-emerald-300">
                {flash}
              </span>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="text-xs font-semibold tracking-wide text-white/40"
              >
                Maybe later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
